/**
 * Reads what an image file is from its first bytes, without decoding it:
 * the real format (whatever the file name or MIME type says), the stored
 * pixel size, the EXIF orientation (JPEG) and whether it is animated.
 *
 * The pixel size lets the decoder shrink while decoding (a 100-megapixel
 * photo never becomes a full-size canvas), and the orientation tells which
 * way round that size is once the photo is upright.
 */

export type SourceKind = 'png' | 'jpeg' | 'gif' | 'webp' | 'heic' | 'avif' | 'bmp' | 'unknown';

export interface SourceInfo {
  kind: SourceKind;
  /** Pixel size as stored, before EXIF orientation; null when the header does not say. */
  width: number | null;
  height: number | null;
  /** EXIF orientation, 1 (as stored) to 8. */
  orientation: number;
  /** More than one frame (GIF, animated WebP, APNG). */
  animated: boolean;
  /** Frames counted (1 for a still; a lower bound when only part of the file was read). */
  frames: number;
}

const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1']);
const AVIF_BRANDS = new Set(['avif', 'avis']);

const ascii = (b: Uint8Array, at: number, n: number): string => {
  let s = '';
  for (let i = at; i < at + n && i < b.length; i++) s += String.fromCharCode(b[i]!);
  return s;
};
const u16be = (b: Uint8Array, i: number) => (b[i]! << 8) | b[i + 1]!;
const u16le = (b: Uint8Array, i: number) => b[i]! | (b[i + 1]! << 8);
const u24le = (b: Uint8Array, i: number) => b[i]! | (b[i + 1]! << 8) | (b[i + 2]! << 16);
const u32be = (b: Uint8Array, i: number) =>
  ((b[i]! << 24) >>> 0) + (b[i + 1]! << 16) + (b[i + 2]! << 8) + b[i + 3]!;
const u32le = (b: Uint8Array, i: number) =>
  b[i]! + (b[i + 1]! << 8) + (b[i + 2]! << 16) + ((b[i + 3]! << 24) >>> 0);

const still = (kind: SourceKind, width: number | null = null, height: number | null = null) =>
  ({ kind, width, height, orientation: 1, animated: false, frames: 1 }) satisfies SourceInfo;

export function sniffSource(b: Uint8Array): SourceInfo {
  if (b.length < 12) return still('unknown');
  if (b[0] === 0x89 && ascii(b, 1, 3) === 'PNG') return png(b);
  if (ascii(b, 0, 3) === 'GIF') return gif(b);
  if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 4) === 'WEBP') return webp(b);
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return jpeg(b);
  if (ascii(b, 4, 4) === 'ftyp') {
    const brands = [ascii(b, 8, 4)];
    const size = Math.min(u32be(b, 0), b.length);
    for (let i = 16; i + 4 <= size; i += 4) brands.push(ascii(b, i, 4));
    if (brands.some((x) => AVIF_BRANDS.has(x))) return still('avif');
    if (brands.some((x) => HEIF_BRANDS.has(x))) return still('heic');
  }
  if (b[0] === 0x42 && b[1] === 0x4d) return still('bmp', u32le(b, 18), Math.abs(u32le(b, 22) | 0));
  return still('unknown');
}

/** The size once EXIF orientation is applied (5 to 8 swap width and height). */
export function orientedSize(info: SourceInfo): { width: number; height: number } | null {
  if (!info.width || !info.height) return null;
  return info.orientation >= 5 && info.orientation <= 8
    ? { width: info.height, height: info.width }
    : { width: info.width, height: info.height };
}

function png(b: Uint8Array): SourceInfo {
  const info = still(
    'png',
    b.length >= 24 ? u32be(b, 16) : null,
    b.length >= 24 ? u32be(b, 20) : null,
  );
  // APNG: an acTL chunk before the first IDAT.
  let i = 8;
  while (i + 8 <= b.length) {
    const len = u32be(b, i);
    const type = ascii(b, i + 4, 4);
    if (type === 'IDAT') break;
    if (type === 'acTL' && i + 12 <= b.length) {
      const frames = u32be(b, i + 8);
      return { ...info, animated: frames > 1, frames: Math.max(1, frames) };
    }
    i += 12 + len;
  }
  return info;
}

function gif(b: Uint8Array): SourceInfo {
  const width = u16le(b, 6);
  const height = u16le(b, 8);
  let i = 13;
  const packed = b[10]!;
  if (packed & 0x80) i += 3 * (1 << ((packed & 7) + 1));
  let frames = 0;
  // Walk the blocks: extensions (0x21) and images (0x2c) are runs of sub-blocks.
  const skipSubBlocks = () => {
    while (i < b.length) {
      const n = b[i]!;
      i += 1;
      if (n === 0) return;
      i += n;
    }
  };
  while (i < b.length) {
    const block = b[i]!;
    if (block === 0x3b) break;
    if (block === 0x21) {
      i += 2;
      skipSubBlocks();
    } else if (block === 0x2c) {
      if (i + 10 > b.length) break;
      const p = b[i + 9]!;
      i += 10;
      if (p & 0x80) i += 3 * (1 << ((p & 7) + 1));
      i += 1; // LZW minimum code size
      skipSubBlocks();
      if (i <= b.length) frames += 1;
    } else break; // not a GIF block: stop counting
  }
  frames = Math.max(1, frames);
  return { kind: 'gif', width, height, orientation: 1, animated: frames > 1, frames };
}

function webp(b: Uint8Array): SourceInfo {
  const chunk = ascii(b, 12, 4);
  if (chunk === 'VP8X' && b.length >= 30) {
    const flags = b[20]!;
    const width = 1 + u24le(b, 24);
    const height = 1 + u24le(b, 27);
    const animated = (flags & 0x02) !== 0;
    let frames = animated ? 0 : 1;
    if (animated) {
      let i = 12;
      while (i + 8 <= b.length) {
        const len = u32le(b, i + 4);
        if (ascii(b, i, 4) === 'ANMF') frames += 1;
        i += 8 + len + (len & 1);
      }
    }
    frames = Math.max(1, frames);
    return {
      kind: 'webp',
      width,
      height,
      orientation: 1,
      animated: animated && frames > 1,
      frames,
    };
  }
  if (chunk === 'VP8 ' && b.length >= 30)
    return still('webp', u16le(b, 26) & 0x3fff, u16le(b, 28) & 0x3fff);
  if (chunk === 'VP8L' && b.length >= 25) {
    const bits = u32le(b, 21);
    return still('webp', (bits & 0x3fff) + 1, ((bits >>> 14) & 0x3fff) + 1);
  }
  return still('webp');
}

function jpeg(b: Uint8Array): SourceInfo {
  let orientation = 1;
  let i = 2;
  while (i + 9 < b.length) {
    if (b[i] !== 0xff) {
      i += 1;
      continue;
    }
    const marker = b[i + 1]!;
    if (
      marker === 0xd8 ||
      marker === 0x01 ||
      marker === 0xff ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      i += marker === 0xff ? 1 : 2;
      continue;
    }
    if (marker === 0xda) break; // start of scan: no size found
    const len = u16be(b, i + 2);
    if (marker === 0xe1 && ascii(b, i + 4, 6) === 'Exif\0\0') {
      orientation = exifOrientation(b, i + 10, Math.min(b.length, i + 2 + len)) ?? orientation;
    }
    const isSof =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof) {
      return {
        kind: 'jpeg',
        height: u16be(b, i + 5),
        width: u16be(b, i + 7),
        orientation,
        animated: false,
        frames: 1,
      };
    }
    i += 2 + len;
  }
  return { ...still('jpeg'), orientation };
}

/** Tag 0x0112 in IFD0 of the TIFF block at `t` (ending at `end`). */
function exifOrientation(b: Uint8Array, t: number, end: number): number | null {
  if (t + 8 > end) return null;
  const le = ascii(b, t, 2) === 'II';
  const r16 = (i: number) => (le ? u16le(b, i) : u16be(b, i));
  const r32 = (i: number) => (le ? u32le(b, i) : u32be(b, i));
  const ifd = t + r32(t + 4);
  if (ifd + 2 > end) return null;
  const n = r16(ifd);
  for (let k = 0; k < n; k++) {
    const e = ifd + 2 + k * 12;
    if (e + 12 > end) return null;
    if (r16(e) === 0x0112) {
      const v = r16(e + 8);
      return v >= 1 && v <= 8 ? v : null;
    }
  }
  return null;
}
